'use client';
import React from 'react';
import { createPortal } from 'react-dom';
import { useLayoutView, LayoutMode } from './LayoutContext';
import { useGlobalSpotlightState, useGlobalSpotlight } from './useGlobalSpotlight';
import { useHostControl } from './HostControlContext';
import { useParticipants, useTracks } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { usePinning } from './PinningContext';
import { useEconomyMode } from './economyMode';
import { useVideoQuality } from './VideoQualityContext';
import { useOptionalScreenQuality } from './ScreenQualityContext';
import { isScreenShareHighAllowed } from './screen-share-profile';
import type { VideoQualityProfile } from './video-quality-profile';
import type { ScreenShareQualityProfile } from './screen-share-profile';

export function LayoutSettingsModal() {
  const { 
    isLayoutModalOpen, closeLayoutModal, 
    layoutMode, setLayoutMode,
    maxTiles, setMaxTilesFromUser,
    hideNonVideo, setHideNonVideo 
  } = useLayoutView();
  const { globalSpotlightTarget } = useGlobalSpotlightState();
  const { toggleGlobalSpotlight, canControlSpotlight } = useGlobalSpotlight();
  const { pinnedTrackRef, setPinnedTrackRef } = usePinning();
  const { economyModeEnabled, setEconomyModeEnabled } = useEconomyMode();
  const { videoQualityProfile, setVideoQualityProfile } = useVideoQuality();
  const screenQuality = useOptionalScreenQuality();
  const allowScreenHigh = isScreenShareHighAllowed();
  const participants = useParticipants();
  const allTracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: true }],
    { onlySubscribed: false }
  );
  const isGlobalSpotlightActive = globalSpotlightTarget !== null;
  const isLocalPinned = pinnedTrackRef !== null;
  const [searchTerm, setSearchTerm] = React.useState('');
  const openedAtRef = React.useRef(0);

  React.useEffect(() => {
    if (isLayoutModalOpen) {
      openedAtRef.current = Date.now();
    }
  }, [isLayoutModalOpen]);

  const handleBackdropClose = React.useCallback(() => {
    if (Date.now() - openedAtRef.current < 250) return;
    closeLayoutModal();
  }, [closeLayoutModal]);
  
  // Encontra o nome do participante destacado globalmente
  const spotlightedParticipant = isGlobalSpotlightActive
    ? participants.find(p => p.identity === globalSpotlightTarget)
    : null;
  const spotlightedName = spotlightedParticipant?.name || spotlightedParticipant?.identity || 'Participante';

  // Encontra o nome do participante fixado localmente
  const pinnedParticipant = isLocalPinned
    ? participants.find(p => p.identity === pinnedTrackRef.participant.identity)
    : null;
  const pinnedName = pinnedParticipant?.name || pinnedTrackRef?.participant?.name || pinnedTrackRef?.participant?.identity || 'Participante';

  // Lista de participantes com vídeo para destacar (apenas para hosts)
  const participantsWithVideo = React.useMemo(() => {
    if (!canControlSpotlight) return [];
    
    const tracksByParticipant = new Map<string, typeof allTracks[0]>();
    allTracks.forEach(track => {
      if (track.source === Track.Source.Camera) {
        const existing = tracksByParticipant.get(track.participant.identity);
        if (!existing || track.publication?.isSubscribed) {
          tracksByParticipant.set(track.participant.identity, track);
        }
      }
    });

    return Array.from(tracksByParticipant.values())
      .map(track => ({
        identity: track.participant.identity,
        name: track.participant.name || track.participant.identity,
        track,
      }))
      .filter(p => p.identity); // Remove participantes sem identidade
  }, [allTracks, canControlSpotlight]);

  // Função para fixar/desfixar participante manualmente
  const handleTogglePin = React.useCallback((participant: typeof participantsWithVideo[0]) => {
    const isCurrentlyPinned = pinnedTrackRef?.participant?.identity === participant.identity;
    const isCurrentlySpotlighted = globalSpotlightTarget === participant.identity;
    
    console.log('[LayoutSettingsModal] handleTogglePin chamado:', {
      participantName: participant.name,
      participantIdentity: participant.identity,
      isCurrentlyPinned,
      pinnedTrackRefIdentity: pinnedTrackRef?.participant?.identity,
      isCurrentlySpotlighted,
      globalSpotlightTarget,
      canControlSpotlight
    });
    
    if (isCurrentlyPinned) {
      // Desfixar
      console.log('[LayoutSettingsModal] ✅ DESFIXANDO:', participant.name);
      
      // Remove o pin local primeiro
      setPinnedTrackRef(null);
      console.log('[LayoutSettingsModal] ✅ setPinnedTrackRef(null) chamado');
      
      // SEMPRE tenta remover o spotlight global se o usuário for host (para garantir que todos voltem ao normal)
      if (canControlSpotlight) {
        console.log('[LayoutSettingsModal] ✅ Removendo spotlight global (canControlSpotlight: true, globalSpotlightTarget:', globalSpotlightTarget, ')');
        toggleGlobalSpotlight(null);
      } else {
        console.log('[LayoutSettingsModal] ⚠️ Não removendo spotlight global - sem permissão (canControlSpotlight: false)');
      }
      
      // Volta para o layout automático se estiver em spotlight
      if (layoutMode === 'spotlight') {
        console.log('[LayoutSettingsModal] ✅ Mudando layout de spotlight para auto');
        setLayoutMode('auto');
      }
    } else {
      // Fixar
      console.log('[LayoutSettingsModal] Fixando:', participant.name);
      setPinnedTrackRef(participant.track);
      // Muda para spotlight quando fixa
      setLayoutMode('spotlight');
    }
  }, [pinnedTrackRef, setPinnedTrackRef, setLayoutMode, layoutMode, globalSpotlightTarget, canControlSpotlight, toggleGlobalSpotlight]);

  // Lista filtrada por busca
  const filteredParticipants = React.useMemo(() => {
    if (!searchTerm.trim()) return participantsWithVideo;
    
    const term = searchTerm.toLowerCase().trim();
    return participantsWithVideo.filter(p => 
      p.name.toLowerCase().includes(term) || 
      p.identity.toLowerCase().includes(term)
    );
  }, [participantsWithVideo, searchTerm]);

  // Debug: verificar estado de pinning (depois de participantsWithVideo ser definido)
  // Comentado para reduzir logs e melhorar performance
  // React.useEffect(() => {
  //   if (pinnedTrackRef) {
  //     console.log('[LayoutSettingsModal] 📌 pinnedTrackRef existe:', pinnedTrackRef);
  //     console.log('[LayoutSettingsModal] 📌 pinnedTrackRef.participant?.identity:', pinnedTrackRef.participant?.identity);
  //     console.log('[LayoutSettingsModal] 📌 pinnedTrackRef.participant?.name:', pinnedTrackRef.participant?.name);
  //     console.log('[LayoutSettingsModal] 📌 participantsWithVideo:', participantsWithVideo.map(p => ({ name: p.name, identity: p.identity })));
  //   } else {
  //     console.log('[LayoutSettingsModal] ❌ pinnedTrackRef é null');
  //   }
  // }, [pinnedTrackRef, participantsWithVideo]);

  if (!isLayoutModalOpen) return null;

  const Option = ({ 
    mode, 
    label, 
    icon, 
    disabled = false, 
    disabledReason 
  }: { 
    mode: LayoutMode
    label: string
    icon: React.ReactNode
    disabled?: boolean
    disabledReason?: string
  }) => (
    <label 
      className={`flex items-center justify-between p-3 rounded transition-all ${
        disabled 
          ? 'opacity-50 cursor-not-allowed' 
          : 'cursor-pointer hover:bg-white/5'
      } ${layoutMode === mode ? 'text-blue-400' : 'text-gray-300'}`}
      title={disabled ? disabledReason : undefined}
    >
      <div className="flex items-center gap-3">
        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${layoutMode === mode ? 'border-blue-400' : 'border-gray-500'}`}>
          {layoutMode === mode && <div className="w-2.5 h-2.5 rounded-full bg-blue-400" />}
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-medium">{label}</span>
          {disabled && disabledReason && (
            <span className="text-[10px] text-gray-500 mt-0.5">{disabledReason}</span>
          )}
        </div>
      </div>
      <div className="opacity-80">{icon}</div>
      <input 
        type="radio" 
        name="layout" 
        value={mode} 
        checked={layoutMode === mode} 
        onChange={() => !disabled && setLayoutMode(mode)} 
        disabled={disabled}
        className="hidden" 
      />
    </label>
  );

  return createPortal(
    <div className="em-modal-overlay" onClick={handleBackdropClose}>
      <div className="em-modal-panel bg-[#1e1e1e] w-[400px] rounded-xl shadow-2xl border border-gray-700 overflow-hidden" onClick={e => e.stopPropagation()}>
        
        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
          <h2 className="text-white font-medium">Ajuste a visualização</h2>
          <button type="button" onClick={closeLayoutModal} className="text-gray-400 hover:text-white">✕</button>
        </div>

        {/* INDICAÇÃO DE SPOTLIGHT GLOBAL ATIVO */}
        {isGlobalSpotlightActive && (
          <div className="p-4 border-b border-gray-700 bg-blue-900/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-blue-400">Destaque Global Ativo</span>
                  <span className="text-xs text-gray-400">Mostrando: {spotlightedName}</span>
                </div>
              </div>
              {canControlSpotlight && (
                <button
                  onClick={() => {
                    toggleGlobalSpotlight(null);
                  }}
                  className="px-3 py-1.5 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                >
                  Remover
                </button>
              )}
            </div>
          </div>
        )}

        <div className="p-2 space-y-1">
          
          {/* AUTOMÁTICO */}
          <Option 
            mode="auto" 
            label="Automático (dinâmico)" 
            icon={
              <svg width="40" height="24" viewBox="0 0 40 24" fill="none" className="bg-gray-800 rounded">
                <rect x="4" y="4" width="10" height="16" rx="1" fill="currentColor" className="text-gray-500"/>
                <rect x="16" y="4" width="10" height="16" rx="1" fill="currentColor" className="text-gray-500"/>
                <rect x="28" y="4" width="8" height="16" rx="1" fill="currentColor" className="text-gray-500"/>
              </svg>
            } 
          />

          {/* MOSAICO (GRID) */}
          <Option 
            mode="grid" 
            label="Mosaico" 
            icon={
              <svg width="40" height="24" viewBox="0 0 40 24" fill="none" className="bg-gray-800 rounded">
                <rect x="4" y="4" width="6" height="6" rx="1" fill="currentColor" className="text-gray-500"/>
                <rect x="12" y="4" width="6" height="6" rx="1" fill="currentColor" className="text-gray-500"/>
                <rect x="20" y="4" width="6" height="6" rx="1" fill="currentColor" className="text-gray-500"/>
                <rect x="28" y="4" width="6" height="6" rx="1" fill="currentColor" className="text-gray-500"/>
                <rect x="4" y="12" width="6" height="6" rx="1" fill="currentColor" className="text-gray-500"/>
                <rect x="12" y="12" width="6" height="6" rx="1" fill="currentColor" className="text-gray-500"/>
                <rect x="20" y="12" width="6" height="6" rx="1" fill="currentColor" className="text-gray-500"/>
                <rect x="28" y="12" width="6" height="6" rx="1" fill="currentColor" className="text-gray-500"/>
              </svg>
            } 
          />

          {/* DESTAQUE (SPOTLIGHT) */}
          <Option 
            mode="spotlight" 
            label={isGlobalSpotlightActive ? "Destaque (Global Ativo)" : "Destaque"} 
            icon={
              <svg width="40" height="24" viewBox="0 0 40 24" fill="none" className="bg-gray-800 rounded">
                <rect x="0" y="0" width="40" height="24" rx="2" fill="currentColor" className="text-gray-500"/>
              </svg>
            }
            disabled={isGlobalSpotlightActive}
            disabledReason={isGlobalSpotlightActive ? "O anfitrião destacou um vídeo para todos" : undefined}
          />

          {/* BARRA LATERAL (SIDEBAR) */}
          <Option 
            mode="sidebar" 
            label="Barra lateral" 
            icon={
              <svg width="40" height="24" viewBox="0 0 40 24" fill="none" className="bg-gray-800 rounded">
                <rect x="4" y="4" width="24" height="16" rx="2" fill="currentColor" className="text-gray-500"/>
                <rect x="30" y="4" width="6" height="4" rx="1" fill="currentColor" className="text-gray-500"/>
                <rect x="30" y="10" width="6" height="4" rx="1" fill="currentColor" className="text-gray-500"/>
                <rect x="30" y="16" width="6" height="4" rx="1" fill="currentColor" className="text-gray-500"/>
              </svg>
            } 
          />
        </div>

        {/* SLIDER DE BLOCOS */}
        <div className="p-4 border-t border-gray-700 space-y-3">
          <div className="flex justify-between text-xs text-gray-400">
            <span>Participantes por página</span>
            <span>{maxTiles}</span>
          </div>
          <p className="text-[10px] text-gray-500">
            Quantos vídeos aparecem no mosaico de cada vez. Em salas grandes, use as setas na parte inferior para navegar entre todos.
          </p>
          <div className="flex items-center gap-3">
            <span className="text-gray-500">⊞</span>
            <input 
              type="range" 
              min="4" max="49" step="1" 
              value={maxTiles} 
              onChange={(e) => setMaxTilesFromUser(Number(e.target.value))}
              className="flex-1 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-500"
            />
            <span className="text-gray-500">▦</span>
          </div>
        </div>

        {/* QUALIDADE DA CÂMERA */}
        <div className="p-4 border-t border-gray-700 space-y-3">
          <div className="flex flex-col">
            <span className="text-sm text-gray-300">Qualidade da câmera</span>
            <span className="text-[10px] text-gray-500 mt-0.5">
              {economyModeEnabled
                ? 'Modo economia ativo: câmera limitada a média (540p), mesmo com máxima salva.'
                : 'Padrão média (540p). Alta e máxima usam mais banda e CPU.'}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {(['medium', 'high', 'max'] as VideoQualityProfile[]).map((profile) => (
              <button
                key={profile}
                type="button"
                onClick={() => setVideoQualityProfile(profile)}
                className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                  videoQualityProfile === profile
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {profile === 'medium' ? 'Média' : profile === 'high' ? 'Alta' : 'Máxima'}
              </button>
            ))}
          </div>
        </div>

        {/* QUALIDADE DA TELA */}
        {screenQuality && (
          <div className="p-4 border-t border-gray-700 space-y-3">
            <div className="flex flex-col">
              <span className="text-sm text-gray-300">Qualidade da tela</span>
              <span className="text-[10px] text-gray-500 mt-0.5">
                {economyModeEnabled
                  ? 'Modo economia: tela em Normal (reduzida), mesmo se Alta estiver salva.'
                  : 'Padrão Normal (até 1080p, nítido para texto). Alta usa mais banda — só se a rede permitir.'}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {(['normal', 'high'] as ScreenShareQualityProfile[]).map((profile) => {
                const disabled = profile === 'high' && !allowScreenHigh;
                return (
                  <button
                    key={profile}
                    type="button"
                    disabled={disabled}
                    onClick={() => screenQuality.setScreenQualityProfile(profile)}
                    className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                      screenQuality.screenQualityProfile === profile
                        ? 'bg-blue-600 text-white'
                        : disabled
                          ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {profile === 'normal' ? 'Normal' : 'Alta'}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* MODO ECONOMIA */}
        <div className="p-4 border-t border-gray-700 flex justify-between items-center gap-3">
          <div className="flex flex-col">
            <span className="text-sm text-gray-300">Modo economia</span>
            <span className="text-[10px] text-gray-500 mt-0.5">
              Reduz câmera e tela, desliga fundo virtual e cancelamento de ruído.
            </span>
          </div>
          <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={economyModeEnabled}
              onChange={(e) => setEconomyModeEnabled(e.target.checked)}
            />
            <div className="w-9 h-5 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600" />
          </label>
        </div>

        {/* TOGGLE OCULTAR SEM VIDEO */}
        <div className="p-4 border-t border-gray-700 flex justify-between items-center">
          <span className="text-sm text-gray-300">Ocultar blocos sem vídeo</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              className="sr-only peer" 
              checked={hideNonVideo}
              onChange={(e) => setHideNonVideo(e.target.checked)}
            />
            <div className="w-9 h-5 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>


        {/* SEÇÃO: DESTACAR VÍDEO PARA TODOS (Apenas para Hosts) */}
        {canControlSpotlight && (
          <div className="p-4 border-t border-gray-700 space-y-3">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span className="text-sm font-medium text-white">Destacar vídeo para todos</span>
            </div>

            {/* 1. INDICADOR DE DESTAQUE ATIVO */}
            {isGlobalSpotlightActive && (
              <div className="bg-blue-900/30 border border-blue-700/50 p-3 rounded flex justify-between items-center gap-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <svg className="w-4 h-4 text-blue-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
                  </svg>
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs text-gray-400">Em destaque:</span>
                    <span className="text-sm font-medium text-blue-400 truncate">{spotlightedName}</span>
                  </div>
                </div>
                <button
                  onClick={() => toggleGlobalSpotlight(null)}
                  className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded transition-colors flex-shrink-0 whitespace-nowrap"
                >
                  Parar Destaque
                </button>
              </div>
            )}

            {/* 2. INPUT DE BUSCA */}
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Buscar participante..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded p-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />

              {/* 3. LISTA DE PARTICIPANTES COM SCROLL */}
              {filteredParticipants.length > 0 ? (
                <div className="border border-gray-700 rounded overflow-hidden max-h-48 overflow-y-auto">
                  {filteredParticipants.map((participant) => {
                    const isCurrentlySpotlighted = globalSpotlightTarget === participant.identity;
                    // Compara por identity do participante
                    const pinnedIdentity = pinnedTrackRef?.participant?.identity;
                    const participantIdentity = participant.identity;
                    const isCurrentlyPinned = pinnedIdentity && pinnedIdentity === participantIdentity;
                    
                    // Debug detalhado para cada participante
                    if (pinnedTrackRef) {
                      console.log('[LayoutSettingsModal] 🔍 Comparando:', {
                        participantName: participant.name,
                        participantIdentity: participantIdentity,
                        pinnedIdentity: pinnedIdentity,
                        match: isCurrentlyPinned,
                        pinnedTrackRef: pinnedTrackRef
                      });
                    }
                    
                    // Debug quando encontrar match
                    if (isCurrentlyPinned) {
                      console.log('[LayoutSettingsModal] ✅ Participante fixado encontrado na lista:', participant.name, participant.identity);
                    }
                    
                    return (
                      <div
                        key={participant.identity}
                        className={`w-full p-2.5 hover:bg-gray-700/50 text-sm transition-colors flex items-center justify-between gap-2 border-b border-gray-800 last:border-b-0 ${
                          isCurrentlySpotlighted
                            ? 'bg-blue-600/20 text-blue-400'
                            : isCurrentlyPinned
                            ? 'bg-purple-600/20 text-purple-400'
                            : 'text-gray-300'
                        }`}
                      >
                        <button
                          onClick={() => {
                            // Se estiver fixado, desfixa ao clicar no nome
                            if (isCurrentlyPinned) {
                              handleTogglePin(participant);
                              return;
                            }
                            
                            // Se for host, destaca globalmente
                            if (canControlSpotlight) {
                              if (isCurrentlySpotlighted) {
                                toggleGlobalSpotlight(null);
                              } else {
                                toggleGlobalSpotlight(participant.identity);
                              }
                            }
                            // Se não estiver fixado, fixa ao clicar no participante
                            handleTogglePin(participant);
                          }}
                          className="flex items-center gap-2 flex-1 min-w-0 text-left"
                        >
                          {/* Avatar/Inicial */}
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 ${
                            isCurrentlySpotlighted
                              ? 'bg-blue-600/30 text-blue-400'
                              : isCurrentlyPinned
                              ? 'bg-purple-600/30 text-purple-400'
                              : 'bg-gray-700 text-gray-400'
                          }`}>
                            {participant.name?.[0]?.toUpperCase() || participant.identity[0]?.toUpperCase() || '?'}
                          </div>
                          <span className="truncate">{participant.name || participant.identity}</span>
                        </button>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {/* Botão de Fixar/Desfixar (sempre visível) */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              console.log('[LayoutSettingsModal] 🗑️ Botão de lixeira/pin clicado para:', participant.name);
                              console.log('[LayoutSettingsModal] Estado atual - isCurrentlyPinned:', isCurrentlyPinned);
                              handleTogglePin(participant);
                            }}
                            className={`p-1.5 rounded transition-colors group ${
                              isCurrentlyPinned
                                ? 'hover:bg-red-600/20'
                                : 'hover:bg-purple-600/20'
                            }`}
                            title={isCurrentlyPinned ? "Desfixar participante" : "Fixar participante"}
                          >
                            {isCurrentlyPinned ? (
                              <svg className="w-4 h-4 text-red-400 group-hover:text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 text-purple-400 group-hover:text-purple-300" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
                              </svg>
                            )}
                          </button>
                          {/* Mostra lixeira mesmo se não estiver na lista filtrada mas estiver fixado */}
                          {!isCurrentlyPinned && pinnedTrackRef && pinnedTrackRef.participant?.identity === participant.identity && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                console.log('[LayoutSettingsModal] Desfixando participante (fallback):', participant.name);
                                setPinnedTrackRef(null);
                              }}
                              className="p-1.5 hover:bg-red-600/20 rounded transition-colors group"
                              title="Desfixar participante"
                            >
                              <svg className="w-4 h-4 text-red-400 group-hover:text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                          {isCurrentlySpotlighted && (
                            <svg className="w-4 h-4 text-blue-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : searchTerm.trim() ? (
                <div className="border border-gray-700 rounded p-4 text-center">
                  <p className="text-xs text-gray-500">Nenhum participante encontrado para &quot;{searchTerm}&quot;</p>
                </div>
              ) : (
                <div className="border border-gray-700 rounded p-4 text-center">
                  <p className="text-xs text-gray-500">Nenhum participante com vídeo disponível</p>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>,
    document.body,
  );
}